import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import toast from 'react-hot-toast'
import api from '../../services/api'

const defaultFormState = {
  email_domain: '',
  provider: 'google',
  client_id: '',
  client_secret: ''
}

export default function AddSSOModal({ isOpen, onClose, onSuccess, initialData }) {
  const [formData, setFormData] = useState(defaultFormState)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const isEditMode = Boolean(initialData?.id)

  const ssoProviders = [
    { value: 'google', label: 'Google' },
    { value: 'microsoft', label: 'Microsoft' },
    { value: 'azure', label: 'Azure' },
    { value: 'okta', label: 'Okta' },
    { value: 'auth0', label: 'Auth0' },
    { value: 'onelogin', label: 'OneLogin' },
    { value: 'pingidentity', label: 'Ping Identity' },
    { value: 'adfs', label: 'ADFS' },
    { value: 'keycloak', label: 'Keycloak' },
    { value: 'jumpcloud', label: 'JumpCloud' },
    { value: 'duo', label: 'Duo' },
    { value: 'sailpoint', label: 'SailPoint' },
    { value: 'saml', label: 'SAML 2.0' },
    { value: 'oidc', label: 'OpenID Connect' }
  ]

  useEffect(() => {
    if (!isOpen) {
      setFormData({ ...defaultFormState })
      setErrors({})
      return
    }

    if (initialData) {
      setFormData({
        email_domain: initialData.email_domain || '',
        provider: initialData.provider || 'google',
        client_id: initialData.client_id || '',
        client_secret: initialData.client_secret || ''
      })
    } else {
      setFormData({ ...defaultFormState })
    }
  }, [initialData, isOpen])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.email_domain.trim()) {
      newErrors.email_domain = 'Email domain is required'
    } else {
      // Basic email domain validation (should be like comptivia.com, esgplaybook.com)
      const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
      if (!domainPattern.test(formData.email_domain.trim())) {
        newErrors.email_domain = 'Please enter a valid email domain (e.g., comptivia.com, esgplaybook.com)'
      }
    }

    if (!formData.client_id.trim()) {
      newErrors.client_id = 'Secret ID is required'
    }

    if (!formData.client_secret.trim()) {
      newErrors.client_secret = 'Client secret is required'
    }

    if (!formData.provider) {
      newErrors.provider = 'SSO provider is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    const normalizedDomain = formData.email_domain.trim()
    const trimmedClientId = formData.client_id.trim()
    const trimmedSecret = formData.client_secret.trim()

    try {
      if (isEditMode) {
        const response = await api.put(`/product-integrations/${initialData.id}`, {
          email_domain: normalizedDomain,
          provider: formData.provider,
          client_id: trimmedClientId,
          client_secret: trimmedSecret
        })

        if (response.data.success) {
          toast.success('SSO configuration updated successfully!')
          if (onSuccess) onSuccess()
          onClose()
        } else {
          toast.error(response.data.message || 'Failed to update SSO configuration')
        }
      } else {
        const response = await api.put('/product-integrations/sso/update-by-domain', {
          email_domain: normalizedDomain,
          client_id: trimmedClientId,
          client_secret: trimmedSecret,
          sso_provider: formData.provider
        })
        
        if (response.data.success) {
          toast.success('SSO configuration saved successfully!')
          if (onSuccess) onSuccess()
          onClose()
        } else {
          toast.error(response.data.message || 'Failed to save SSO configuration')
        }
      }
    } catch (error) {
      console.error('Error updating SSO:', error)
      toast.error(error.response?.data?.message || 'Failed to update SSO configuration')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{isEditMode ? 'Edit SSO Configuration' : 'Add SSO Configuration'}</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email_domain" className="block text-sm font-medium text-secondary-700 mb-2">
                Email Domain *
              </label>
              <Input
                id="email_domain"
                name="email_domain"
                type="text"
                value={formData.email_domain}
                onChange={handleInputChange}
                placeholder="comptivia.com, esgplaybook.com"
                className={errors.email_domain ? 'border-red-500' : ''}
                disabled={isSubmitting}
                required
              />
              {errors.email_domain && (
                <p className="text-red-500 text-sm mt-1">{errors.email_domain}</p>
              )}
              <p className="text-secondary-500 text-xs mt-1">
                Enter the email domain (e.g., comptivia.com, esgplaybook.com)
              </p>
            </div>

            <div>
              <label htmlFor="provider" className="block text-sm font-medium text-secondary-700 mb-2">
                SSO Provider *
              </label>
              <select
                id="provider"
                name="provider"
                value={formData.provider}
                onChange={handleInputChange}
                className={`input w-full ${errors.provider ? 'border-red-500' : ''}`}
                disabled={isSubmitting}
                required
              >
                {ssoProviders.map((provider) => (
                  <option key={provider.value} value={provider.value}>
                    {provider.label}
                  </option>
                ))}
              </select>
              {errors.provider && (
                <p className="text-red-500 text-sm mt-1">{errors.provider}</p>
              )}
            </div>

            <div>
              <label htmlFor="client_id" className="block text-sm font-medium text-secondary-700 mb-2">
                Secret ID *
              </label>
              <Input
                id="client_id"
                name="client_id"
                type="text"
                value={formData.client_id}
                onChange={handleInputChange}
                placeholder="Enter secret ID"
                className={errors.client_id ? 'border-red-500' : ''}
                disabled={isSubmitting}
                required
              />
              {errors.client_id && (
                <p className="text-red-500 text-sm mt-1">{errors.client_id}</p>
              )}
            </div>

            <div>
              <label htmlFor="client_secret" className="block text-sm font-medium text-secondary-700 mb-2">
                Client Secret *
              </label>
              <Input
                id="client_secret"
                name="client_secret"
                type="password"
                value={formData.client_secret}
                onChange={handleInputChange}
                placeholder="Enter client secret"
                className={errors.client_secret ? 'border-red-500' : ''}
                disabled={isSubmitting}
                required
              />
              {errors.client_secret && (
                <p className="text-red-500 text-sm mt-1">{errors.client_secret}</p>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (isEditMode ? 'Saving...' : 'Updating...') : isEditMode ? 'Save Changes' : 'Update SSO'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

